import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, TrendingDown, Award, AlertTriangle, Target, Users, MapPin, Calendar, Building, DollarSign } from 'lucide-react';

const WindsorMarketIntelligence = () => {
  const [activeTab, setActiveTab] = useState('projects');
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRealData = async () => {
      try {
        const response = await fetch('/api/dashboard-data/windsor-final-dashboard', { credentials: 'same-origin' });
        if (response.ok) {
          const data = await response.json();
          setMarketData(data);
        } else {
          console.error('Failed to load Windsor market data');
        }
      } catch (error) {
        console.error('Error loading Windsor market data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadRealData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading Windsor Market Intelligence...</div>
      </div>
    );
  }

  if (!marketData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Failed to load market data</div>
      </div>
    );
  }

  const { overview, projectTypes, contractors, locations, quarterlyTrends, stages } = marketData;

  const StatCard = ({ title, value, icon: Icon, trend, subtitle }) => (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </div>
        <div className="p-3 bg-blue-50 rounded-full">
          <Icon className="h-6 w-6 text-blue-600" />
        </div>
      </div>
      {trend && (
        <div className="mt-2 flex items-center">
          {trend > 0 ? (
            <TrendingUp className="h-4 w-4 text-green-500" />
          ) : (
            <TrendingDown className="h-4 w-4 text-red-500" />
          )}
          <span className={`text-sm ml-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {Math.abs(trend).toFixed(1)}%
          </span>
        </div>
      )}
    </div>
  );

  const formatCurrency = (value) => {
    if (value >= 1000000000) {
      return `$${(value / 1000000000).toFixed(1)}B`;
    } else if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  const formatNumber = (value) => {
    return value.toLocaleString();
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Windsor Market Intelligence</h1>
          <p className="text-gray-600">Real-time construction market analysis based on 749 projects (2018-2025)</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Projects"
            value={formatNumber(overview.totalProjects)}
            icon={Building}
            subtitle="All tracked projects"
          />
          <StatCard
            title="Total Value"
            value={formatCurrency(overview.totalValue)}
            icon={DollarSign}
            subtitle="Combined project value"
          />
          <StatCard
            title="Active Projects"
            value={overview.activeProjects}
            icon={Target}
            subtitle="Construction Underway"
          />
          <StatCard
            title="Completed Projects"
            value={overview.completedProjects}
            icon={Award}
            subtitle="Awarded & Post-Bid"
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'projects', label: 'Project Types', icon: Building },
                { id: 'contractors', label: 'Top Contractors', icon: Users },
                { id: 'locations', label: 'Locations', icon: MapPin },
                { id: 'stages', label: 'Project Stages', icon: Calendar }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">


            {/* Project Types Tab */}
            {activeTab === 'projects' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold">Top Project Types by Value</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={projectTypes}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="type" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="value" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {projectTypes.map((project, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-sm mb-2">{project.type}</h4>
                      <div className="space-y-1 text-sm">
                        <p>Projects: {project.count}</p>
                        <p>Total Value: {formatCurrency(project.value)}</p>
                        <p>Avg Value: {formatCurrency(project.avgValue)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contractors Tab */}
            {activeTab === 'contractors' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold">Top Contractors by Project Value</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={contractors}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={120} />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="value" fill="#10B981" />
                  </BarChart>
                </ResponsiveContainer>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {contractors.map((contractor, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-sm mb-2">{contractor.name}</h4>
                      <div className="space-y-1 text-sm">
                        <p>Projects: {contractor.projects}</p>
                        <p>Total Value: {formatCurrency(contractor.value)}</p>
                        <p>Market Share: {contractor.marketShare.toFixed(1)}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Locations Tab */}
            {activeTab === 'locations' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold">Projects by Location</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={locations}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="location" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="value" fill="#F59E0B" />
                  </BarChart>
                </ResponsiveContainer>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {locations.map((location, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-sm mb-2">{location.location}</h4>
                      <div className="space-y-1 text-sm">
                        <p>Projects: {location.projects}</p>
                        <p>Total Value: {formatCurrency(location.value)}</p>
                        <p>Avg Value: {formatCurrency(location.avgValue)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}



            {/* Stages Tab */}
            {activeTab === 'stages' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold">Projects by Stage</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={stages}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="stage" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="value" fill="#8B5CF6" />
                  </BarChart>
                </ResponsiveContainer>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {stages.map((stage, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-sm mb-2">{stage.stage}</h4>
                      <div className="space-y-1 text-sm">
                        <p>Projects: {stage.count}</p>
                        <p>Total Value: {formatCurrency(stage.value)}</p>
                        <p>Avg Value: {formatCurrency(stage.avgValue)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WindsorMarketIntelligence; 